import { useState, useRef, useEffect } from "react";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Theme from "@amzn/meridian/esm/components/theme";
import { PRE_STREAM_FALLBACK_MESSAGE, PROMPT_TYPE } from "../assistant.constants";
import { ANNOUNCEMENTS } from "../../announcements/announcements.constants";
import { ROLES, BUILT_IN_AGENT } from "../../../constants";
import { THEME_COLOR } from "../../../styles/colors";
import AssistantOptions from "../AssistantOptions";
import ChatInlineAlert from "./errors/ChatInlineAlert";
import ChatInput from "./ChatInput";
import ChatMessages from "./ChatMessages";
import StartedByStatus from "./StartedByStatus";
import InMeetingExpFeedbackPopover from "../InMeetingExpFeedbackPopover";
import AnnouncementToast from "../../announcements/AnnouncementToast";
import LogManager from "../../../utils/logManager";
import { invokeAgentStream } from "../assistant.utils";
import { fetchActiveAgents } from "../../../utils/agenticExtensionUtils";
import { fetchWhosWho, parseExternalInvitees } from "../../whosWho/whosWho.utils";
import { useSession } from "../../../context/SessionContext";
import { useFeatureFlags } from "../../../context/FeatureFlagsContext";

const Assistant = ({
  startedBy,
  dismissStartedBy,
  chatMessages,
  setChatMessages,
  isChatLoading,
  setIsChatLoading,
  enabledAgentIds,
  availableExtensions = [],
  onAgentsRefreshed,
  publishedContentStore,
  inviteeAliases,
  externalInvitees,
}) => {
  const { role, meetingUuid, meetingId, rtmsStreamId, alias, rtmsStartTime } = useSession();
  const { isWhosWhoEnabled } = useFeatureFlags();
  const [showOptions, setShowOptions] = useState(true);
  // Pre-stream / pre-send error message shown as an inline alert above the input.
  const [inlineAlert, setInlineAlert] = useState(null);
  const [feedbackPopoverOpen, setFeedbackPopoverOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(BUILT_IN_AGENT.id);

  const hasInvokePermission = (invokePermission) => {
    if (!invokePermission || invokePermission === "all") return true;
    if (invokePermission === "host") return role === ROLES.ORGANIZER;
    if (invokePermission === "host_and_alt") return role === ROLES.ORGANIZER || role === ROLES.ALTERNATIVE_HOST;
    return true;
  };

  const enabledAgents = [BUILT_IN_AGENT];
  for (const ext of availableExtensions) {
    if (enabledAgentIds?.includes(ext.extensionId) && ext.canChat && hasInvokePermission(ext.invokePermission)) {
      enabledAgents.push({
        id: ext.extensionId,
        name: ext.displayName || ext.defaultDisplayName,
        shortName: ext.shortName,
        extensionId: ext.extensionId,
      });
    }
  }

  const selectedAgent = enabledAgents.find((a) => a.id === selectedAgentId) || enabledAgents[0] || BUILT_IN_AGENT;

  // Drop the selection when the host disables the selected agent mid-meeting. The picker
  // only falls back for display, so a stale selectedAgentId would silently re-select that
  // agent if the host turned it back on.
  useEffect(() => {
    if (selectedAgent.id !== selectedAgentId) {
      setSelectedAgentId(selectedAgent.id);
    }
  }, [selectedAgent.id, selectedAgentId]);
  const feedbackSubmitRef = useRef(null);
  const sessionIdRef = useRef(`${alias || "user"}-${Date.now()}-${crypto.randomUUID()}`);
  const abortControllerRef = useRef(null);

  // TODO: Remove this when queueing fucntionality is added
  const handleWhosWhoAnnouncementAction = () => {
    if (!isChatLoading) {
      handleSend("Who's who", PROMPT_TYPE.WHOS_WHO);
      return;
    }
    // If Who's Who is already in flight, skip — it will complete on its own.
    if (chatMessages.some((msg) => msg.whosWho && msg.isLoading)) return;
    // Otherwise an agent stream is in flight — abort it and queue Who's Who.
    abortControllerRef.current?.abort();
    pendingWhosWhoRef.current = true;
  };

  // After an abort settles and isChatLoading flips to false, fire the pending Who's Who.
  const pendingWhosWhoRef = useRef(false);
  useEffect(() => {
    if (!isChatLoading && pendingWhosWhoRef.current) {
      pendingWhosWhoRef.current = false;
      handleSend("Who's who", PROMPT_TYPE.WHOS_WHO);
    }
  }, [isChatLoading]);

  const handleFeedbackMessage = (message, agent) => {
    setChatMessages((prev) => [...prev, { role: "agent", text: message, agent, agentId: agent?.id, createdAt: Date.now() }]);
  };

  const handleOpenFeedbackPopover = (submitCallback) => {
    feedbackSubmitRef.current = submitCallback;
    setFeedbackPopoverOpen(true);
  };

  const handleFeedbackPopoverSubmit = (comment) => {
    if (feedbackSubmitRef.current) {
      feedbackSubmitRef.current(comment);
    }
  };

  const handleFeedbackPopoverClose = () => {
    setFeedbackPopoverOpen(false);
    feedbackSubmitRef.current = null;
  };

  const handleSend = async (message, promptType = PROMPT_TYPE.FREEFORM) => {
    if (isChatLoading) return;
    setInlineAlert(null);

    if (promptType === PROMPT_TYPE.WHOS_WHO) {
      setShowOptions(false);
      setIsChatLoading(true);
      setChatMessages((prev) => [
        ...prev,
        { role: "user", text: message, promptType, agentId: BUILT_IN_AGENT.id, agent: BUILT_IN_AGENT, createdAt: Date.now() },
        {
          role: "agent",
          agentId: BUILT_IN_AGENT.id,
          whosWho: true,
          isLoading: true,
          createdAt: Date.now(),
        },
      ]);
      const startedAt = Date.now();

      try {
        const result = await fetchWhosWho({
          meetingUuid: meetingUuid || "",
          attendees: inviteeAliases || "",
          externalInvitees: externalInvitees || "",
        });
        setChatMessages((prev) => prev.map((msg) =>
          msg.whosWho && msg.isLoading
            ? { ...msg, isLoading: false, invitees: result.invitees || [] }
            : msg
        ));
        LogManager.metric("Assistant_WhosWho_Success", 1, alias ? { alias } : {});
        LogManager.metric("Assistant_WhosWho_Latency", Date.now() - startedAt,
          alias ? { alias } : {});
      } catch (err) {
        LogManager.error("Who's Who call failed:", err);
        LogManager.metric("Assistant_WhosWho_Error", 1, alias ? { alias } : {});
        const fallbackInvitees = [
          ...(inviteeAliases || "")
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean)
            .map((a) => ({ id: a, alias: a, name: a, initials: a.slice(0, 2).toUpperCase() })),
          ...parseExternalInvitees(externalInvitees),
        ];
        setChatMessages((prev) => prev.map((msg) =>
          msg.whosWho && msg.isLoading
            ? { ...msg, isLoading: false, isError: true, invitees: fallbackInvitees }
            : msg
        ));
      } finally {
        setIsChatLoading(false);
      }
      return;
    }

    const agentId = promptType === PROMPT_TYPE.FREEFORM ? selectedAgent.id : BUILT_IN_AGENT.id;

    // Guard against a double-send while the check below awaits.
    setIsChatLoading(true);

    // Attendees see host toggles only when reopening the picker, so the selection may be
    // stale. Re-check rather than talk to a disabled agent. Runs before any chat state is
    // touched so a dropped send leaves no trace in the transcript.
    if (agentId !== BUILT_IN_AGENT.id && role !== ROLES.ORGANIZER && meetingUuid) {
      const activeAgents = await fetchActiveAgents(meetingUuid);
      // A null result means the check itself failed — let the send proceed and rely on the
      // backend to reject a disabled agent, rather than blocking on a network blip.
      if (activeAgents) {
        if (onAgentsRefreshed) onAgentsRefreshed(activeAgents);
        if (!activeAgents.some((a) => a.extensionId === agentId)) {
          setInlineAlert("Agent's not active yet — try again shortly. Or ask the organizer to turn it on.");
          LogManager.info(`Dropped send to ${agentId}: no longer active for this meeting.`);
          setSelectedAgentId(BUILT_IN_AGENT.id);
          setIsChatLoading(false);
          return;
        }
      }
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setShowOptions(false);
    const targetAgent = enabledAgents.find((a) => a.id === agentId) || BUILT_IN_AGENT;
    setChatMessages((prev) => [...prev, { role: "user", text: message, promptType, agentId, agent: targetAgent, createdAt: Date.now() }]);

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      LogManager.info(`Resolved user timezone: ${timezone}`);
      const requestBody = {
        prompt: message,
        sessionId: sessionIdRef.current,
        meetingMeta: {
          meetingId: meetingId || "",
          meetingUuid: meetingUuid || "",
          rtmsStreamId: rtmsStreamId || "",
          rtmsStartTime,
          timezone,
        },
      };
      if (role) requestBody.role = role;

      if (targetAgent && targetAgent.extensionId) {
        requestBody.extensionId = targetAgent.extensionId;
      }

      await invokeAgentStream({ requestBody, signal: controller.signal, setChatMessages, agentId, agent: targetAgent, alias });
    } catch (err) {
      if (err.name === "AbortError") return;
      LogManager.error("Agent call failed:", err);
      LogManager.metric("Assistant_ServerError_Shown", 1, alias ? { alias } : {});
      const errorMessage = err.serverMessage;
      // Pre-stream errors surface as an inline alert above the input, not an
      // in-conversation bubble. Fall back to generic copy when the body wasn't parseable.
      if (err.phase === "pre-stream") {
        setInlineAlert(errorMessage || PRE_STREAM_FALLBACK_MESSAGE);
      } else {
        setChatMessages((prev) => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          const lastMsg = updated[updated.length - 1];
          if (lastMsg && lastMsg.role === "agent" && !lastMsg.text) {
            updated[updated.length - 1] = { ...lastMsg, isError: true, errorMessage, createdAt: lastMsg.createdAt || Date.now() };
          } else {
            updated.push({ role: "agent", text: "", isError: true, errorMessage, agentId, agent: targetAgent, createdAt: Date.now() });
          }
          return updated;
        });
      }
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleNewSession = () => {
    abortControllerRef.current?.abort();
    setShowOptions(true);
    setInlineAlert(null);
    setChatMessages([]);
    publishedContentStore?.clear?.();
    dismissStartedBy();
    setIsChatLoading(false);
  };

  const publishedItems = publishedContentStore?.contentList || [];
  const hasMessages = chatMessages.length > 0 || publishedItems.length > 0;

  return (
    <Column spacing="none" height="calc(100vh - 131px)" heights={["fill", "fit"]}>
      <Column spacing="none" overflowY="auto">
        <Theme tokens={{ alertLinkColorMedium: THEME_COLOR.TEAL_GREEN }}>
          <AnnouncementToast
            announcement={ANNOUNCEMENTS.WHOS_WHO}
            onAction={handleWhosWhoAnnouncementAction}
            visible={isWhosWhoEnabled}
          />
        </Theme>
        {hasMessages && (
          <Row alignmentHorizontal="center" spacing="none">
            <ChatMessages
              messages={chatMessages}
              isLoading={isChatLoading}
              sessionId={sessionIdRef.current}
              aimsUuid={rtmsStreamId}
              meetingId={meetingId}
              meetingUuid={meetingUuid}
              onFeedbackMessage={handleFeedbackMessage}
              onOpenFeedbackPopover={handleOpenFeedbackPopover}
              enabledAgents={enabledAgents}
              publishedContentStore={publishedContentStore}
              startedBy={startedBy}
            />
          </Row>
        )}
      </Column>

      <Row alignmentHorizontal="center" spacing="none">
        <Column spacing="none" spacingInset="none none 350 none">
          {/* With messages, the "started by" status is in the chat log instead */}
          {!hasMessages && startedBy && <StartedByStatus startedBy={startedBy} />}
          {showOptions && !hasMessages && (
            <Column spacingInset="none none 400 none">
              <AssistantOptions onOptionSelect={handleSend} alias={alias} isWhosWhoEnabled={isWhosWhoEnabled} />
            </Column>
          )}
          {feedbackPopoverOpen && (
            <InMeetingExpFeedbackPopover
              onSubmit={handleFeedbackPopoverSubmit}
              onClose={handleFeedbackPopoverClose}
            />
          )}
          <ChatInlineAlert message={inlineAlert} onClose={() => setInlineAlert(null)} />
          <ChatInput
            onSend={handleSend}
            onNewSession={handleNewSession}
            disabled={isChatLoading}
            agents={enabledAgents}
            selectedAgent={selectedAgent}
            onSelectAgent={setSelectedAgentId}
            role={role}
            meetingUuid={meetingUuid}
            onAgentsRefreshed={onAgentsRefreshed}
          />
        </Column>
      </Row>
    </Column>
  );
};

export default Assistant;
